import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import RecipeCard from '../components/RecipeCard';
import { RevealItem } from '../components/Reveal';
import SectionBg from '../components/SectionBg';
import ProductsCarousel from '../components/ProductsCarousel';
import { HeartDoodle, WaveDivider, SocialBadge } from '../components/brand/Brand';
import { topRated, latest, popular, categories, budgetPresets, occasions } from '../data/recipes';
import heroImg from '../assets/images/template/710627370_1319014817031028_5275639585233349245_n.jpg';
import familyImg from '../assets/images/template/702320618_1309315181334325_4241859877794734326_n.jpeg';

const gridStagger = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.15 },
  variants: { show: { transition: { staggerChildren: 0.08 } } },
};
const heroText = {
  initial: { opacity: 0, x: -24 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};
const heroMedia = {
  initial: { opacity: 0, scale: 0.94 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
};

// Silhouette de cœur pleine, utilisée comme masque pour la photo du héro.
const HEART_SVG = encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 29.6'><path d='M23.6 0c-3.4 0-6.3 2.7-7.6 5.6C14.7 2.7 11.8 0 8.4 0 3.8 0 0 3.8 0 8.4c0 9.4 9.5 11.9 16 21.2 6.1-8.8 16-12.1 16-21.2C32 3.8 28.2 0 23.6 0z' fill='black'/></svg>"
);
const heartMask = `url("data:image/svg+xml,${HEART_SVG}") center / contain no-repeat`;

const HomePage = () => {
  const navigate = useNavigate();
  const [budget, setBudget] = useState(3000);
  const [moment, setMoment] = useState('');

  const eligible = popular.concat(topRated).filter(
    (r, i, arr) =>
      arr.findIndex((x) => x.id === r.id) === i &&
      r.estimated_cost <= budget &&
      (!moment || (r.occasions || []).includes(moment))
  ).length;

  const searchBudget = (e) => {
    e.preventDefault();
    const q = new URLSearchParams({ max: String(budget) });
    if (moment) q.set('occasion', moment);
    navigate(`/budget?${q.toString()}`);
  };

  return (
    <>
      {/* ---------- HÉRO ---------- */}
      <Hero>
        <SectionBg tone="chocolate" variant={0} />
        <HeroInner>
          <HeroText as={motion.div} {...heroText}>
            <Eyebrow>
              <i className="ti ti-sparkles" aria-hidden="true" /> Par amour, on crée plus
            </Eyebrow>
            <HeroTitle>
              Des milliers de recettes <Hl>à la Tartina</Hl>, pour tous les budgets et moments.
            </HeroTitle>
            <HeroLead>
              Explore, cuisine et partage des idées gourmandes au bon goût chocolat
              unique de Tartina. Trouve la recette parfaite selon ton budget et tente
              ta chance au jeu-concours.
            </HeroLead>
            <HeroActions>
              <PrimaryBtn to="/recipes">
                <i className="ti ti-tools-kitchen-2" aria-hidden="true" /> Explorer les recettes
              </PrimaryBtn>
              <GhostBtn to="/concours">
                <i className="ti ti-trophy" aria-hidden="true" /> Voir le concours
              </GhostBtn>
            </HeroActions>

            <Stats>
              <Stat><b>1 200+</b><span>recettes</span></Stat>
              <Dot />
              <Stat><b>dès 1 000</b><span>FCFA</span></Stat>
              <Dot />
              <Stat><b>15 000+</b><span>gourmands</span></Stat>
            </Stats>
          </HeroText>

          <HeroMedia as={motion.div} {...heroMedia}>
            <HeartFrame>
              <HeroPhoto src={heroImg} alt="Maman et sa fille en cuisine avec Tartina" />
            </HeartFrame>
            <DoodleTL><HeartDoodle size={42} /></DoodleTL>
          </HeroMedia>
        </HeroInner>
        <WaveDivider color="#FFFBF2" />
      </Hero>

      {/* ---------- RECHERCHE PAR BUDGET (IA) ---------- */}
      <Budget>
        <SectionBg tone="yellow" variant={1} />
        <BudgetCard
          as={motion.form}
          onSubmit={searchBudget}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <BudgetHead>
            <AiBadge><i className="ti ti-sparkles" aria-hidden="true" /> Assisté par IA</AiBadge>
            <h2>Ton budget, ton moment — on trouve la recette</h2>
            <p>
              Indique combien tu veux dépenser et pour quelle occasion : notre IA classe les
              meilleures recettes Tartina réalisables, du goûter express au gâteau de fête.
            </p>
          </BudgetHead>

          <Presets>
            {budgetPresets.map((p) => (
              <PresetBtn
                key={p}
                type="button"
                $active={budget === p}
                onClick={() => setBudget(p)}
              >
                ≤ {p.toLocaleString('fr-FR')} FCFA
              </PresetBtn>
            ))}
          </Presets>

          <SliderRow>
            <i className="ti ti-coin" aria-hidden="true" />
            <input
              type="range"
              min="500"
              max="10000"
              step="500"
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
            <BudgetValue>{budget.toLocaleString('fr-FR')} FCFA</BudgetValue>
          </SliderRow>

          <MomentLabel><i className="ti ti-calendar-heart" aria-hidden="true" /> Pour quel moment ?</MomentLabel>
          <Moments>
            <MomentChip type="button" $active={!moment} onClick={() => setMoment('')}>
              Tous les moments
            </MomentChip>
            {occasions.map((o) => (
              <MomentChip
                key={o}
                type="button"
                $active={moment === o}
                onClick={() => setMoment(moment === o ? '' : o)}
              >
                {o.charAt(0).toUpperCase() + o.slice(1)}
              </MomentChip>
            ))}
          </Moments>

          <BudgetFooter>
            <Hint>
              <i className="ti ti-bulb" aria-hidden="true" /> {eligible} recette{eligible > 1 ? 's' : ''} dans ce budget
              {moment ? ` pour « ${moment} »` : ''}
            </Hint>
            <SearchBtn type="submit">
              Trouver mes recettes <i className="ti ti-arrow-right" aria-hidden="true" />
            </SearchBtn>
          </BudgetFooter>
        </BudgetCard>
      </Budget>

      {/* ---------- RECETTES VEDETTES ---------- */}
      <Section>
        <SectionBg tone="yellow" variant={2} />
        <SectionHead>
          <div>
            <Kicker>La sélection</Kicker>
            <h2>Recettes les mieux notées</h2>
          </div>
          <MoreLink to="/recipes">Tout voir <i className="ti ti-arrow-right" aria-hidden="true" /></MoreLink>
        </SectionHead>
        <Grid as={motion.div} {...gridStagger}>
          {topRated.map((r) => (
            <RevealItem key={r.id}><RecipeCard recipe={r} /></RevealItem>
          ))}
        </Grid>
      </Section>

      {/* ---------- CATÉGORIES ---------- */}
      <Section>
        <SectionBg tone="chocolate" variant={0} />
        <SectionHead>
          <div>
            <Kicker>Envie de quoi ?</Kicker>
            <h2>Parcourir par catégorie</h2>
          </div>
        </SectionHead>
        <CatGrid as={motion.div} {...gridStagger}>
          {categories.map((c) => (
            <RevealItem key={c.key}>
              <CatCard to={`/recipes?category=${encodeURIComponent(c.key)}`}>
                <CatIcon><i className={`ti ${c.icon}`} aria-hidden="true" /></CatIcon>
                <CatLabel>{c.label}</CatLabel>
                <CatCount>{c.count} recettes</CatCount>
              </CatCard>
            </RevealItem>
          ))}
        </CatGrid>
      </Section>

      {/* ---------- BANDE JEU-CONCOURS ---------- */}
      <Contest>
        <SectionBg tone="white" variant={2} />
        <WaveDivider color="#E30613" flip />
        <ContestInner>
          <ContestText
            as={motion.div}
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <Eyebrow $light>
              <i className="ti ti-trophy" aria-hidden="true" /> Jeu-concours Tartina
            </Eyebrow>
            <h2>Propose ta recette. Fais-la voter. Gagne.</h2>
            <p>
              Soumets ta création à base de Tartina : les recettes les mieux notées et les
              plus populaires sont sélectionnées comme gagnantes chaque mois.
            </p>
            <ContestActions>
              <ContestPrimary to="/add-recipe?concours=1">
                <i className="ti ti-upload" aria-hidden="true" /> Soumettre ma recette
              </ContestPrimary>
              <ContestGhost to="/concours">Voir le classement</ContestGhost>
            </ContestActions>
          </ContestText>

          <Leaderboard
            as={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <LbTitle><i className="ti ti-flame" aria-hidden="true" /> Au top ce mois-ci</LbTitle>
            {popular.slice(0, 3).map((r, i) => (
              <LbRow key={r.id} to={`/recipes/${r.id}`}>
                <Rank $rank={i + 1}>{i + 1}</Rank>
                <LbThumb src={r.image} alt="" />
                <LbInfo>
                  <b>{r.name}</b>
                  <small><i className="ti ti-heart" aria-hidden="true" /> {r.votes.toLocaleString('fr-FR')} votes · ★ {r.rating.toFixed(1)}</small>
                </LbInfo>
              </LbRow>
            ))}
          </Leaderboard>
        </ContestInner>
        <WaveDivider color="#FFFBF2" />
      </Contest>

      {/* ---------- NOS FORMATS TARTINA ---------- */}
      <Section>
        <SectionBg tone="yellow" variant={0} />
        <SectionHead>
          <div>
            <Kicker>La gamme</Kicker>
            <h2>Un format Tartina pour chaque envie</h2>
          </div>
        </SectionHead>
        <ProductsCarousel />
      </Section>

      {/* ---------- DERNIÈRES RECETTES ---------- */}
      <Section>
        <SectionBg tone="yellow" variant={1} />
        <SectionHead>
          <div>
            <Kicker>Tout frais</Kicker>
            <h2>Dernières recettes ajoutées</h2>
          </div>
          <MoreLink to="/recipes">Tout voir <i className="ti ti-arrow-right" aria-hidden="true" /></MoreLink>
        </SectionHead>
        <Grid as={motion.div} {...gridStagger}>
          {latest.map((r) => (
            <RevealItem key={r.id}><RecipeCard recipe={r} /></RevealItem>
          ))}
        </Grid>
      </Section>

      {/* ---------- QUALITÉ TARTINA ---------- */}
      <Quality>
        <SectionBg tone="chocolate" variant={1} />
        <QualityPhoto
          as={motion.img}
          src={familyImg}
          alt="Une famille réunie autour de Tartina"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
        <QualityText
          as={motion.div}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <Kicker>La qualité Tartina</Kicker>
          <h2>Un goût chocolat unique, pour créer des moments de bonheur en famille</h2>
          <p>
            Des ingrédients soigneusement sélectionnés et une texture onctueuse :
            Tartina, c'est le plaisir du chocolat à partager en famille, du
            petit-déjeuner au dessert.
          </p>
          <SocialBadge />
        </QualityText>
      </Quality>
    </>
  );
};

/* ---------------- styles ---------------- */
const Hero = styled.section`
  background: ${({ theme }) => theme.colors.secondary};
  position: relative;
  isolation: isolate;
`;
const HeroInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 56px 24px 24px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 40px;
  align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; padding-top: 36px; }
`;
const HeroText = styled.div``;
const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ $light, theme }) => ($light ? 'rgba(255,255,255,0.2)' : theme.colors.primary)};
  color: #fff;
  font-weight: 800;
  font-size: 1.8rem;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  margin-bottom: 18px;
`;
const HeroTitle = styled.h1`
  font-size: 2.2rem;
  color: ${({ theme }) => theme.colors.chocolate};
  font-weight: 700;
  margin-bottom: 16px;
`;
const Hl = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;
const HeroLead = styled.p`
  font-size: 1.08rem;
  color: ${({ theme }) => theme.colors.chocolate};
  opacity: 0.9;
  max-width: 52ch;
  margin: 0 0 26px;
`;
const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
const btn = `
  display: inline-flex; align-items: center; gap: 8px;
  text-decoration: none; font-weight: 800; font-size: 1rem;
  padding: 14px 24px; border-radius: 999px; transition: all .2s ease;
`;
const PrimaryBtn = styled(Link)`
  ${btn}
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  &:hover { background: ${({ theme }) => theme.colors.primaryDark}; transform: translateY(-2px); }
`;
const GhostBtn = styled(Link)`
  ${btn}
  background: ${({ theme }) => theme.colors.chocolate};
  color: #fff;
  &:hover { transform: translateY(-2px); }
`;
const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 30px;
`;
const Stat = styled.div`
  display: flex;
  flex-direction: column;
  b { font-family: ${({ theme }) => theme.fonts.heading}; font-size: 1.4rem; color: ${({ theme }) => theme.colors.chocolate}; }
  span { font-size: 0.82rem; color: ${({ theme }) => theme.colors.tertiary}; font-weight: 700; }
`;
const Dot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
`;
const HeroMedia = styled.div`
  position: relative;
`;
const HeartFrame = styled.div`
  aspect-ratio: 1 / 1;
  background: #fff;
  padding: 12px;
  -webkit-mask: ${heartMask};
  mask: ${heartMask};
  filter: drop-shadow(0 18px 30px rgba(74, 38, 0, 0.22));
`;
const HeroPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-mask: ${heartMask};
  mask: ${heartMask};
`;
const FloatCard = styled.div`
  position: absolute;
  left: -10px;
  bottom: 22px;
  background: #fff;
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  b { font-size: 0.92rem; color: ${({ theme }) => theme.colors.chocolate}; display: block; }
  small { font-size: 0.76rem; color: ${({ theme }) => theme.colors.textSecondary}; }
`;
const Star = styled.span`
  color: ${({ theme }) => theme.colors.star};
  font-size: 1.6rem;
`;
const DoodleTL = styled.span`
  position: absolute;
  top: -18px;
  right: 24px;
  transform: rotate(12deg);
  @media (max-width: 900px) { display: none; }
`;

const Budget = styled.section`
  background: ${({ theme }) => theme.colors.background};
  padding: 8px 24px 8px;
  margin-top: -8px;
  position: relative;
  isolation: isolate;
`;
const BudgetCard = styled.form`
  max-width: 1000px;
  margin: -28px auto 0;
  position: relative;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 28px 32px;
`;
const BudgetHead = styled.div`
  text-align: center;
  margin-bottom: 18px;
  h2 { font-size: 1.7rem; color: ${({ theme }) => theme.colors.chocolate}; margin: 10px 0 6px; }
  p { margin: 0 auto; max-width: 60ch; color: ${({ theme }) => theme.colors.textSecondary}; font-size: 0.96rem; }
`;
const AiBadge = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${({ theme }) => theme.colors.cream};
  color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 800; font-size: 0.78rem;
  padding: 6px 14px; border-radius: 999px;
`;
const Presets = styled.div`
  display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-bottom: 18px;
`;
const PresetBtn = styled.button`
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.chocolate)};
  font-weight: 800; font-size: 0.9rem;
  padding: 9px 16px; border-radius: 999px; cursor: pointer;
  transition: all .15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;
const SliderRow = styled.div`
  display: flex; align-items: center; gap: 14px;
  background: ${({ theme }) => theme.colors.background};
  padding: 14px 18px; border-radius: 16px;
  i { font-size: 1.3rem; color: ${({ theme }) => theme.colors.secondaryDark}; }
  input[type='range'] { flex: 1; accent-color: ${({ theme }) => theme.colors.primary}; }
`;
const BudgetValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700; font-size: 1.15rem; min-width: 130px; text-align: right;
  color: ${({ theme }) => theme.colors.primary};
`;
const MomentLabel = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin: 16px 0 10px; font-weight: 800; color: ${({ theme }) => theme.colors.chocolate};
  i { color: ${({ theme }) => theme.colors.primary}; font-size: 1.1rem; }
`;
const Moments = styled.div`
  display: flex; flex-wrap: wrap; justify-content: center; gap: 10px;
`;
const MomentChip = styled.button`
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.secondaryDark : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : '#fff')};
  color: ${({ theme }) => theme.colors.chocolate};
  font-weight: 800; font-size: 0.88rem; padding: 8px 15px; border-radius: 999px; cursor: pointer;
  text-transform: capitalize; transition: all .15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.secondaryDark}; }
`;
const BudgetFooter = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; margin-top: 18px; flex-wrap: wrap;
`;
const Hint = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  color: ${({ theme }) => theme.colors.textSecondary}; font-size: 0.9rem; font-weight: 700;
  i { color: ${({ theme }) => theme.colors.secondaryDark}; }
`;
const SearchBtn = styled.button`
  ${btn}
  border: none; cursor: pointer;
  background: ${({ theme }) => theme.colors.primary}; color: #fff;
  &:hover { background: ${({ theme }) => theme.colors.primaryDark}; }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 8px;
  position: relative;
  isolation: isolate;
`;
const SectionHead = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between;
  margin-bottom: 24px; gap: 16px;
  h2 { font-size: 1.9rem; color: ${({ theme }) => theme.colors.chocolate}; }
`;
const Kicker = styled.span`
  display: block; color: ${({ theme }) => theme.colors.primary};
  font-weight: 800; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px;
  margin-bottom: 6px;
`;
const MoreLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 6px;
  color: ${({ theme }) => theme.colors.tertiary}; text-decoration: none; font-weight: 800;
  white-space: nowrap;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 26px;
`;
const CatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 18px;
`;
const CatCard = styled(Link)`
  text-decoration: none; text-align: center;
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px; padding: 26px 16px;
  transition: all .2s ease;
  &:hover { transform: translateY(-4px); box-shadow: ${({ theme }) => theme.shadows.md}; border-color: ${({ theme }) => theme.colors.secondary}; }
`;
const CatIcon = styled.div`
  width: 60px; height: 60px; margin: 0 auto 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.cream};
  display: flex; align-items: center; justify-content: center;
  i { font-size: 1.7rem; color: ${({ theme }) => theme.colors.tertiary}; }
`;
const CatLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 600; font-size: 1.1rem; color: ${({ theme }) => theme.colors.chocolate};
`;
const CatCount = styled.div`
  font-size: 0.82rem; color: ${({ theme }) => theme.colors.textSecondary}; margin-top: 2px;
`;

const Contest = styled.section`
  background: ${({ theme }) => theme.colors.primary};
  margin-top: 48px;
  position: relative;
  isolation: isolate;
`;
const ContestInner = styled.div`
  max-width: 1200px; margin: 0 auto; padding: 16px 24px 24px;
  display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 40px; align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const ContestText = styled.div`
  color: #fff;
  h2 { color: #fff; font-size: clamp(1.8rem, 3.4vw, 2.6rem); margin-bottom: 14px; }
  p { color: rgba(255,255,255,0.92); max-width: 50ch; margin: 0 0 22px; font-size: 1.02rem; }
`;
const ContestActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px;
`;
const ContestPrimary = styled(Link)`
  ${btn}
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.chocolate};
  &:hover { background: ${({ theme }) => theme.colors.secondaryDark}; transform: translateY(-2px); }
`;
const ContestGhost = styled(Link)`
  ${btn}
  background: rgba(255,255,255,0.15); color: #fff;
  border: 2px solid rgba(255,255,255,0.5);
  &:hover { background: rgba(255,255,255,0.25); }
`;
const Leaderboard = styled.div`
  background: #fff; border-radius: 22px; padding: 18px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;
const LbTitle = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-family: ${({ theme }) => theme.fonts.heading}; font-weight: 600; font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.chocolate}; margin-bottom: 12px;
  i { color: ${({ theme }) => theme.colors.primary}; }
`;
const LbRow = styled(Link)`
  display: flex; align-items: center; gap: 12px;
  padding: 10px; border-radius: 14px; text-decoration: none;
  transition: background .15s ease;
  &:hover { background: ${({ theme }) => theme.colors.background}; }
  & + & { margin-top: 4px; }
`;
const Rank = styled.span`
  flex: none; width: 30px; height: 30px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 0.9rem;
  background: ${({ $rank, theme }) =>
    $rank === 1 ? theme.colors.secondary : $rank === 2 ? '#E9E2D2' : '#F3D9C0'};
  color: ${({ theme }) => theme.colors.chocolate};
`;
const LbThumb = styled.img`
  width: 46px; height: 46px; border-radius: 12px; object-fit: cover; flex: none;
`;
const LbInfo = styled.div`
  min-width: 0;
  b { display: block; font-size: 0.95rem; color: ${({ theme }) => theme.colors.chocolate}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  small { display: inline-flex; align-items: center; gap: 5px; font-size: 0.8rem; color: ${({ theme }) => theme.colors.textSecondary}; }
  i { color: ${({ theme }) => theme.colors.primary}; }
`;

const Quality = styled.section`
  max-width: 1200px; margin: 48px auto 0; padding: 0 24px; position: relative; isolation: isolate;
  display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const QualityPhoto = styled.img`
  width: 100%; border-radius: 24px; aspect-ratio: 1/1; object-fit: cover;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;
const QualityText = styled.div`
  h2 { font-size: 1.9rem; color: ${({ theme }) => theme.colors.chocolate}; margin: 6px 0 14px; }
  p { color: ${({ theme }) => theme.colors.textSecondary}; font-size: 1.02rem; margin: 0 0 20px; max-width: 50ch; }
`;

export default HomePage;
